import type { EventManager } from '@arkturian/audio-guide';
import type { InterestOption } from '@arkturian/audio-guide';
import { AVAILABLE_INTERESTS } from '@arkturian/audio-guide';
import type { IView } from '../IView';

/**
 * InterestsView — chip-row UI for multi-selecting user interests in
 * Generic mode. Renders AVAILABLE_INTERESTS as toggle-chips inside
 * #interestsChipsRow. Visual state syncs to InterestsState via the
 * 'interests:changed' bus event.
 *
 * Chip styling kept inline for now (Tokyo-Night palette, matches the
 * rest of the skin). Selected chips get a filled bg + accent border;
 * unselected get a transparent bg + muted border.
 *
 * Emits user:interest-toggle with { slug } on click. The controller
 * delegates to InterestsState.toggle(slug), which then emits
 * interests:changed → this view re-renders chip state.
 */
export class InterestsView implements IView {
  private _root: HTMLElement | null = null;
  private _bus: EventManager | null = null;
  private _clickHandler: ((ev: Event) => void) | null = null;

  private _onModelChanged = (payload: { slugs: string[] }): void => {
    this._render(new Set(payload.slugs));
  };

  mount(root: HTMLElement): void {
    this._root = root.querySelector<HTMLElement>('#interestsChipsRow');
    if (!this._root) return;
    // Initial render — all chips unselected. The controller's boot-sync
    // emit fixes the visual state to match the model right after.
    this._render(new Set());
  }

  bindEvents(bus: EventManager): void {
    this._bus = bus;
    if (this._root) {
      this._clickHandler = (ev: Event) => {
        const target = ev.target as HTMLElement | null;
        const chip = target?.closest<HTMLElement>('[data-interest-slug]');
        if (!chip) return;
        const slug = chip.dataset.interestSlug;
        if (!slug) return;
        bus.emit('user:interest-toggle', { slug });
      };
      this._root.addEventListener('click', this._clickHandler);
    }
    bus.on('interests:changed', this._onModelChanged);
  }

  dispose(): void {
    if (this._root && this._clickHandler) {
      this._root.removeEventListener('click', this._clickHandler);
      this._clickHandler = null;
    }
    if (this._bus) {
      this._bus.off('interests:changed', this._onModelChanged);
      this._bus = null;
    }
  }

  /** Re-render all chips with the selected-state from `selected`. */
  private _render(selected: Set<string>): void {
    if (!this._root) return;
    this._root.innerHTML = AVAILABLE_INTERESTS
      .map((opt) => this._chipHtml(opt, selected.has(opt.slug)))
      .join('');
  }

  /**
   * Inline-styled chip. Active = filled #7aa2f7 bg, white text.
   * Inactive = transparent bg, muted text + border. Cursor pointer
   * on both, micro tap-feedback via :active in CSS would be a future
   * polish.
   */
  private _chipHtml(opt: InterestOption, active: boolean): string {
    const bg = active ? '#7aa2f7' : 'transparent';
    const color = active ? '#1a1b26' : '#c0caf5';
    const border = active ? '#7aa2f7' : '#414868';
    return (
      `<span data-interest-slug="${opt.slug}" ` +
      `style="display:inline-block;padding:6px 12px;margin:3px;` +
      `background:${bg};color:${color};border:1px solid ${border};` +
      `border-radius:14px;font-size:13px;font-weight:600;` +
      `cursor:pointer;user-select:none;transition:all 120ms ease">` +
      `${opt.emoji} ${opt.label}</span>`
    );
  }
}
